import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Users, AlertTriangle, Building2, Factory, KeyRound, Mail, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ROLE_LABEL = { bayi: "Bayi", uretici: "Üretici", admin: "Admin" };
const ROLE_COLOR = { bayi: "border-blue-300 bg-blue-50 text-blue-700", uretici: "border-amber-300 bg-amber-50 text-amber-700", admin: "border-zinc-300 bg-zinc-50 text-zinc-700" };

const RoleBadge = ({ role }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] uppercase tracking-wider font-mono border ${ROLE_COLOR[role] || ROLE_COLOR.bayi}`} data-testid={`role-badge-${role}`}>
        {role === "uretici" ? <Factory className="w-3 h-3" strokeWidth={1.5} /> : <Building2 className="w-3 h-3" strokeWidth={1.5} />}
        {ROLE_LABEL[role] || role}
    </span>
);

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("bayi");
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [resetTarget, setResetTarget] = useState(null);
    const [resetPwd, setResetPwd] = useState("");
    const [resetBusy, setResetBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password || !name.trim()) {
            toast.error("Kullanıcı adı, şifre ve isim zorunludur");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/users", { username: username.trim(), password, name: name.trim(), email: email.trim(), role });
            toast.success(`${ROLE_LABEL[role]} oluşturuldu`);
            setUsername(""); setPassword(""); setName(""); setEmail(""); setRole("bayi");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/users/${deleteTarget.id}`);
            toast.success(`${ROLE_LABEL[deleteTarget.role] || "Kullanıcı"} silindi`);
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const doResetPassword = async () => {
        if (!resetTarget || !resetPwd || resetPwd.length < 4) {
            toast.error("Şifre en az 4 karakter olmalı");
            return;
        }
        setResetBusy(true);
        try {
            await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
            toast.success(`${resetTarget.name} için şifre güncellendi`);
            setResetTarget(null);
            setResetPwd("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResetBusy(false);
        }
    };

    return (
        <div className="p-6 md:p-10 lg:p-12 max-w-[1400px] animate-fade-up" data-testid="users-page">
            <div className="mb-10">
                <div className="tag-label mb-3">Yönetim</div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-2">
                    Kullanıcı Yönetimi.
                </h1>
                <p className="text-zinc-600 max-w-xl">Bayi ve üretici hesaplarını oluşturun, mevcut kullanıcıları yönetin.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Form */}
                <form onSubmit={submit} className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="user-form">
                    <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <div className="tag-label">Yeni Kullanıcı Oluştur</div>
                        <UserPlus className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                    </div>
                    <div className="p-6 space-y-5">
                        <div className="space-y-2">
                            <Label className="tag-label">Rol</Label>
                            <Select value={role} onValueChange={setRole}>
                                <SelectTrigger className="rounded-none h-12 border-zinc-300 focus:ring-0 focus:border-black" data-testid="user-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="bayi" className="rounded-none">Bayi</SelectItem>
                                    <SelectItem value="uretici" className="rounded-none">Üretici</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="user-username">Kullanıcı Adı</Label>
                            <Input id="user-username" value={username} onChange={(e) => setUsername(e.target.value)} className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="user-username-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="user-password">Şifre</Label>
                            <Input id="user-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="user-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="user-name">{role === "uretici" ? "Üretici / Firma Adı" : "Bayi / Firma Adı"}</Label>
                            <Input id="user-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="user-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="user-email">E-posta (opsiyonel)</Label>
                            <Input id="user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black" data-testid="user-email-input" />
                        </div>
                    </div>
                    <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50">
                        <Button type="submit" disabled={submitting} className="w-full h-12 rounded-none bg-black hover:bg-zinc-800 text-white font-semibold" data-testid="user-submit-button">
                            {submitting ? "Oluşturuluyor..." : `${role === "uretici" ? "ÜRETİCİ" : "BAYİ"} OLUŞTUR`}
                        </Button>
                    </div>
                </form>

                {/* List */}
                <div className="lg:col-span-3 border border-zinc-200 bg-white">
                    <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                        <div className="tag-label">Kayıtlı Kullanıcılar ({users.length})</div>
                        <Users className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                    </div>
                    {users.length === 0 ? (
                        <div className="p-16 text-center">
                            <Users className="w-10 h-10 mx-auto text-zinc-300 mb-3" strokeWidth={1} />
                            <div className="text-sm text-zinc-500">Henüz kayıtlı kullanıcı yok.</div>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-zinc-200 hover:bg-transparent">
                                    <TableHead className="tag-label h-11">Kullanıcı</TableHead>
                                    <TableHead className="tag-label">Rol</TableHead>
                                    <TableHead className="tag-label">Firma</TableHead>
                                    <TableHead className="tag-label">E-posta</TableHead>
                                    <TableHead className="tag-label">Oluşturulma</TableHead>
                                    <TableHead className="tag-label text-right">Aksiyon</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow key={u.id} className="border-zinc-200" data-testid={`user-row-${u.id}`}>
                                        <TableCell className="font-mono text-xs">@{u.username}</TableCell>
                                        <TableCell><RoleBadge role={u.role} /></TableCell>
                                        <TableCell className="text-sm font-medium">{u.name}</TableCell>
                                        <TableCell className="text-sm text-zinc-600">{u.email || "-"}</TableCell>
                                        <TableCell className="text-sm">{new Date(u.created_at).toLocaleDateString("tr-TR")}</TableCell>
                                        <TableCell className="text-right space-x-1">
                                            <Button onClick={() => { setResetTarget(u); setResetPwd(""); }} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300" data-testid={`user-reset-${u.id}`} title="Şifre sıfırla">
                                                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            </Button>
                                            <Button onClick={() => setDeleteTarget(u)} variant="outline" size="sm" className="rounded-none border-zinc-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300" data-testid={`user-delete-${u.id}`} title="Sil">
                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            <SmtpTestSection />

            <SystemPurgeSection />

            <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPwd(""); } }}>
                <DialogContent className="rounded-none border-zinc-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                            <KeyRound className="w-5 h-5" strokeWidth={1.5} />
                            Şifre Sıfırla
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="px-3 py-2 bg-zinc-50 border border-zinc-200">
                            <div className="tag-label mb-0.5">Kullanıcı</div>
                            <div className="font-semibold text-sm">{resetTarget?.name} <span className="text-zinc-500 font-mono text-xs">@{resetTarget?.username}</span></div>
                        </div>
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="reset-pwd">Yeni Şifre</Label>
                            <Input
                                id="reset-pwd"
                                type="text"
                                value={resetPwd}
                                onChange={(e) => setResetPwd(e.target.value)}
                                placeholder="Yeni şifreyi girin (en az 4 karakter)"
                                className="rounded-none h-12 border-zinc-300 focus-visible:ring-0 focus-visible:border-black font-mono"
                                autoFocus
                                data-testid="reset-pwd-input"
                            />
                            <p className="text-xs text-zinc-500">Yeni şifreyi kullanıcıya iletmeyi unutmayın. Eski şifre artık çalışmayacak.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setResetTarget(null); setResetPwd(""); }} disabled={resetBusy} className="rounded-none">Vazgeç</Button>
                        <Button onClick={doResetPassword} disabled={resetBusy || resetPwd.length < 4} className="rounded-none bg-black hover:bg-zinc-800 text-white" data-testid="reset-pwd-submit">
                            {resetBusy ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent className="rounded-none border-zinc-200">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display tracking-tight">Kullanıcıyı sil?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>@{deleteTarget?.username}</strong> kullanıcısı kalıcı olarak silinecek. Bu işlem geri alınamaz.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="user-delete-confirm">
                            Sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const SmtpTestSection = () => {
    const [status, setStatus] = useState(null);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/admin/smtp-status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    }, []);

    const runTest = async () => {
        setTesting(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/smtp-test");
            setResult(data);
            if (data.ok) toast.success("Test e-postası gönderildi — gelen kutusunu kontrol edin");
            else toast.error("Gönderim başarısız — detayları aşağıda inceleyin");
        } catch (e) {
            setResult({ ok: false, message: formatApiError(e) });
            toast.error(formatApiError(e));
        } finally {
            setTesting(false);
        }
    };

    const Row = ({ label, value, mono = true }) => (
        <div className="flex items-baseline gap-3 py-1.5 border-b border-zinc-100 last:border-0">
            <span className="tag-label w-32 shrink-0">{label}</span>
            <span className={`text-sm ${mono ? "font-mono" : ""}`}>{value ?? "—"}</span>
        </div>
    );

    return (
        <div className="mt-12" data-testid="smtp-test-section">
            <div className="border border-zinc-200 bg-white">
                <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-zinc-700" strokeWidth={1.5} />
                        <div>
                            <div className="tag-label mb-1">E-posta</div>
                            <h2 className="font-display text-xl font-bold tracking-tight">SMTP Yapılandırması</h2>
                        </div>
                    </div>
                </div>

                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Config */}
                    <div>
                        <div className="tag-label mb-3">Aktif Yapılandırma</div>
                        {status === null ? (
                            <div className="text-sm text-zinc-500">Yükleniyor...</div>
                        ) : (
                            <div className="border border-zinc-200 bg-zinc-50 p-4">
                                <Row label="Yapılandırılmış" value={status.configured ? "Evet ✓" : "Hayır ✗"} mono={false} />
                                <Row label="Host" value={status.host} />
                                <Row label="Port" value={status.port} />
                                <Row label="Kullanıcı" value={status.user} />
                                <Row label="Gönderici" value={status.from} />
                                <Row label="Şifre Tanımlı" value={status.password_set ? "Evet ✓" : "Hayır ✗"} mono={false} />
                                <Row label="Cert doğrulama" value={status.validate_certs ? "Açık" : "Kapalı (lenient)"} mono={false} />
                            </div>
                        )}
                    </div>

                    {/* Test */}
                    <div>
                        <div className="tag-label mb-3">Test Gönderimi</div>
                        <div className="border border-zinc-200 p-4 space-y-4">
                            <p className="text-sm text-zinc-600 leading-relaxed">
                                Aşağıdaki butona tıklayarak admin e-posta adresine bir test maili gönderebilirsiniz.
                                Gönderim sonucu (başarı / hata mesajı) burada görünür.
                            </p>
                            <Button
                                onClick={runTest}
                                disabled={testing}
                                className="rounded-none h-11 px-6 bg-black hover:bg-zinc-800 text-white"
                                data-testid="smtp-test-button"
                            >
                                <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                {testing ? "Gönderiliyor..." : "Test E-postası Gönder"}
                            </Button>

                            {result && (
                                <div className={`border p-3 ${result.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                                    <div className="flex items-start gap-2">
                                        {result.ok ? (
                                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                                        )}
                                        <div className="min-w-0">
                                            <div className={`text-sm font-semibold ${result.ok ? "text-emerald-700" : "text-red-700"}`}>
                                                {result.ok ? "Gönderim başarılı" : "Gönderim başarısız"}
                                            </div>
                                            {result.to && <div className="text-xs text-zinc-600 mt-1">Alıcı: <span className="font-mono">{result.to}</span></div>}
                                            <div className={`text-xs mt-2 ${result.ok ? "text-emerald-700" : "text-red-700"} font-mono break-words`}>
                                                {result.message}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SystemPurgeSection = () => {
    const [purgeOrders, setPurgeOrders] = useState(false);
    const [purgeWarranties, setPurgeWarranties] = useState(false);
    const [purgeFiles, setPurgeFiles] = useState(false);
    const [purgeDealers, setPurgeDealers] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const anySelected = purgeOrders || purgeWarranties || purgeFiles || purgeDealers;

    const doPurge = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/purge", {
                purge_orders: purgeOrders,
                purge_warranties: purgeWarranties,
                purge_files: purgeFiles,
                purge_dealers: purgeDealers,
            });
            const parts = Object.entries(data.deleted).map(([k, v]) => `${v} ${k}`).join(", ");
            toast.success(`Temizlendi: ${parts || "0 kayıt"}`);
            setPurgeOrders(false);
            setPurgeWarranties(false);
            setPurgeFiles(false);
            setPurgeDealers(false);
            setConfirmOpen(false);
            // Hard reload so user list updates
            setTimeout(() => window.location.reload(), 600);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const Item = ({ id, checked, onChange, label, desc }) => (
        <label
            htmlFor={id}
            className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border ${checked ? "border-red-500 bg-red-50" : "border-zinc-200 hover:bg-zinc-50"}`}
            data-testid={`purge-${id}`}
        >
            <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="rounded-none mt-0.5 border-zinc-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
            <div className="min-w-0">
                <div className="font-semibold text-sm">{label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
            </div>
        </label>
    );

    return (
        <div className="mt-12" data-testid="system-purge-section">
            <div className="border border-red-300 bg-white">
                <div className="px-6 py-4 border-b border-red-300 bg-red-50/40 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                        <div>
                            <div className="tag-label text-red-700 mb-1">Tehlikeli Bölge</div>
                            <h2 className="font-display text-xl font-bold tracking-tight">Sistem Temizliği</h2>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <p className="text-sm text-zinc-600 max-w-2xl">
                        Aşağıda işaretlediğiniz kategorilerdeki <strong>tüm kayıtlar kalıcı olarak silinir</strong>. Bu işlem geri alınamaz. Test verilerini temizlemek veya sistemi sıfırdan başlatmak için kullanın.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Item id="orders" checked={purgeOrders} onChange={setPurgeOrders}
                            label="Tüm siparişleri sil"
                            desc="Tüm bayilerin oluşturduğu sipariş kayıtları silinir." />
                        <Item id="warranties" checked={purgeWarranties} onChange={setPurgeWarranties}
                            label="Tüm garantileri sil"
                            desc="Tüm bayilerin oluşturduğu garanti kayıtları silinir." />
                        <Item id="files" checked={purgeFiles} onChange={setPurgeFiles}
                            label="Yüklenen dosya kayıtlarını sil"
                            desc="Garanti araç fotoğrafı kayıtları silinir (DB ref)." />
                        <Item id="dealers" checked={purgeDealers} onChange={setPurgeDealers}
                            label="Tüm bayi kullanıcılarını sil"
                            desc="Admin (caner) hesabı korunur. Bayi hesapları silinir." />
                    </div>

                    <div className="pt-2">
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            disabled={!anySelected || busy}
                            className="rounded-none h-11 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
                            data-testid="purge-submit-button"
                        >
                            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Seçilenleri Kalıcı Olarak Sil
                        </Button>
                    </div>
                </div>
            </div>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent className="rounded-none border-zinc-200">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display tracking-tight">Emin misiniz?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <span>Aşağıdaki kategorilerdeki tüm kayıtlar <strong>kalıcı olarak</strong> silinecek:</span>
                            <ul className="list-disc list-inside text-zinc-700 mt-2">
                                {purgeOrders && <li>Tüm siparişler</li>}
                                {purgeWarranties && <li>Tüm garantiler</li>}
                                {purgeFiles && <li>Yüklenen dosya kayıtları</li>}
                                {purgeDealers && <li>Tüm bayi kullanıcıları</li>}
                            </ul>
                            <span className="block pt-2 text-red-600 font-semibold">Bu işlem geri alınamaz.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" disabled={busy}>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doPurge}
                            disabled={busy}
                            className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                            data-testid="purge-confirm"
                        >
                            {busy ? "Siliniyor..." : "Evet, sil"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default UsersPage;
